import { z } from "zod/v4";

export const emailStatusSchema = z.enum(["sent", "delivered", "bounced", "failed"]);

export const emailCategorySchema = z.enum([
	"transactional",
	"game_reminders",
	"game_updates",
	"club_updates",
	"achievements",
	"newsletters",
	"marketing",
	"court_alerts",
]);

export const emailLogSchema = z.object({
	created_at: z.coerce.date(),
	error: z.string().nullable(),
	id: z.string().uuid(),
	is_test: z.boolean(),
	resend_id: z.string().nullable(),
	status: emailStatusSchema,
	subject: z.string(),
	template: z.string(),
	to_email: z.string(),
	user_id: z.string().uuid().nullable(),
});

export const emailTemplateVolumeSchema = z.object({
	count: z.number(),
	template: z.string(),
});

export const emailStatsSchema = z.object({
	bounced: z.number(),
	by_template: z.array(emailTemplateVolumeSchema),
	delivered: z.number(),
	failed: z.number(),
	last_7d: z.number(),
	sent: z.number(),
	total: z.number(),
});

export const emailTemplateSchema = z.object({
	category: emailCategorySchema,
	key: z.string(),
	locales: z.array(z.string()),
});

export const sendTestEmailSchema = z.object({
	locale: z.enum(["de", "en"]).optional(),
	subject: z.string().optional(),
	template: z.string().min(1, "Template is required"),
	to: z.string().email("A valid email is required"),
});

export const sendPlayerEmailSchema = z.discriminatedUnion("kind", [
	z.object({
		kind: z.literal("compose"),
		locale: z.enum(["de", "en"]).optional(),
		message: z.string().trim().min(1, "Message is required"),
		subject: z.string().trim().min(1, "Subject is required"),
		user_id: z.string().uuid(),
	}),
	z.object({
		kind: z.literal("template"),
		locale: z.enum(["de", "en"]).optional(),
		template: z.enum(["welcome"]),
		user_id: z.string().uuid(),
	}),
]);

export const sendTestResultSchema = z.object({
	error: z.string().nullable(),
	id: z.string().uuid(),
	resend_id: z.string().nullable(),
	status: z.enum(["sent", "failed"]),
});

export const newsletterSubscriberSchema = z.object({
	created_at: z.coerce.date(),
	email: z.string(),
	id: z.string().uuid(),
	locale: z.string(),
});

export type EmailStatus = z.infer<typeof emailStatusSchema>;
export type EmailCategory = z.infer<typeof emailCategorySchema>;
export type EmailLog = z.infer<typeof emailLogSchema>;
export type EmailStats = z.infer<typeof emailStatsSchema>;
export type EmailTemplateVolume = z.infer<typeof emailTemplateVolumeSchema>;
export type EmailTemplate = z.infer<typeof emailTemplateSchema>;
export type SendTestEmail = z.infer<typeof sendTestEmailSchema>;
export type SendTestResult = z.infer<typeof sendTestResultSchema>;
export type SendPlayerEmail = z.infer<typeof sendPlayerEmailSchema>;
export type NewsletterSubscriber = z.infer<typeof newsletterSubscriberSchema>;
